import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FilterOptions, OpportunityType, ContractType } from '@/types';
import { arrondissements } from '@/data/mockData';
import { RotateCcw } from 'lucide-react';

interface OpportunityFiltersProps {
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
}

const opportunityTypes: { value: OpportunityType; label: string }[] = [
  { value: 'emploi', label: 'Emploi' },
  { value: 'stage', label: 'Stage' },
  { value: 'bourse', label: 'Bourse' },
  { value: 'formation', label: 'Formation' },
  { value: 'volontariat', label: 'Volontariat' }
];

const contractTypes: { value: ContractType; label: string }[] = [
  { value: 'cdi', label: 'CDI' },
  { value: 'cdd', label: 'CDD' },
  { value: 'temps-plein', label: 'Temps plein' },
  { value: 'temps-partiel', label: 'Temps partiel' },
  { value: 'freelance', label: 'Freelance' }
];

export function OpportunityFilters({ filters, onFiltersChange }: OpportunityFiltersProps) {
  const handleTypeChange = (type: OpportunityType, checked: boolean) => {
    const currentTypes = filters.type || [];
    const newTypes = checked
      ? [...currentTypes, type]
      : currentTypes.filter(t => t !== type);
    
    onFiltersChange({ ...filters, type: newTypes.length > 0 ? newTypes : undefined });
  };

  const handleContractChange = (contract: ContractType, checked: boolean) => {
    const currentContracts = filters.contractType || [];
    const newContracts = checked
      ? [...currentContracts, contract]
      : currentContracts.filter(c => c !== contract);
    
    onFiltersChange({ ...filters, contractType: newContracts.length > 0 ? newContracts : undefined });
  };

  const handleLocationChange = (location: string, checked: boolean) => {
    const currentLocations = filters.location || [];
    const newLocations = checked
      ? [...currentLocations, location]
      : currentLocations.filter(l => l !== location);
    
    onFiltersChange({ ...filters, location: newLocations.length > 0 ? newLocations : undefined });
  };

  const resetFilters = () => {
    onFiltersChange({});
  };

  return (
    <Card className="p-6 sticky top-20">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-poppins font-semibold text-foreground">Filtres</h2>
        <Button variant="ghost" size="sm" onClick={resetFilters}>
          <RotateCcw className="h-4 w-4 mr-1" />
          Réinitialiser
        </Button>
      </div>
      
      {/* Type Filter */}
      <div className="mb-6">
        <h3 className="font-poppins font-medium text-foreground mb-3">Type d'opportunité</h3>
        <div className="space-y-2">
          {opportunityTypes.map(({ value, label }) => (
            <div key={value} className="flex items-center space-x-2">
              <Checkbox
                id={value}
                checked={filters.type?.includes(value) || false}
                onCheckedChange={(checked) => handleTypeChange(value, checked as boolean)}
              />
              <Label
                htmlFor={value}
                className="text-sm font-inter text-muted-foreground cursor-pointer hover:text-foreground"
              >
                {label}
              </Label>
            </div>
          ))}
        </div>
      </div>
      
      {/* Contract Type Filter */}
      <div className="mb-6">
        <h3 className="font-poppins font-medium text-foreground mb-3">Type de contrat</h3>
        <div className="space-y-2">
          {contractTypes.map(({ value, label }) => (
            <div key={value} className="flex items-center space-x-2">
              <Checkbox
                id={value}
                checked={filters.contractType?.includes(value) || false}
                onCheckedChange={(checked) => handleContractChange(value, checked as boolean)}
              />
              <Label
                htmlFor={value}
                className="text-sm font-inter text-muted-foreground cursor-pointer hover:text-foreground"
              >
                {label}
              </Label>
            </div>
          ))}
        </div>
      </div>
      
      {/* Location Filter */}
      <div>
        <h3 className="font-poppins font-medium text-foreground mb-3">Arrondissement</h3>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {arrondissements.map((location) => (
            <div key={location} className="flex items-center space-x-2">
              <Checkbox
                id={location}
                checked={filters.location?.includes(location) || false}
                onCheckedChange={(checked) => handleLocationChange(location, checked as boolean)}
              />
              <Label
                htmlFor={location}
                className="text-sm font-inter text-muted-foreground cursor-pointer hover:text-foreground"
              >
                {location}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}